import React from 'react';
import { useContentfulImage } from '../../hooks/useContentful';

interface ContentfulImageProps {
  imageKey?: string;
  fallbackSrc: string;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  loading?: 'lazy' | 'eager';
  sizes?: string;
  onLoad?: () => void;
  onError?: () => void;
}

export const ContentfulImage: React.FC<ContentfulImageProps> = ({
  imageKey,
  fallbackSrc,
  alt,
  className,
  style,
  loading = 'lazy',
  sizes,
  onLoad,
  onError
}) => {
  const { data: imageData } = useContentfulImage(imageKey || '');
  
  // Get image data from Contentful
  const contentfulImage = imageData?.[0] as any;
  const imageAsset = contentfulImage?.fields as any;
  
  // Safely access nested image fields
  const imageField = imageAsset?.image as any;
  const imageUrl = imageField?.fields?.file?.url;
  
  // Determine final image source and alt text
  const imageSrc = imageUrl ? `https:${imageUrl}` : fallbackSrc;
    
  const imageAlt = String(imageAsset?.altText || alt || '');
  const imageCaption = imageAsset?.caption ? String(imageAsset.caption) : undefined;
  const imageCredit = imageAsset?.credit ? String(imageAsset.credit) : undefined;
  
  // Get responsive variants if available
  const variants = imageAsset?.variants || {};
  const focalPointData = imageAsset?.focalPoint as any || {};
  const focalPoint = { 
    x: Number(focalPointData.x) || 0.5, 
    y: Number(focalPointData.y) || 0.5 
  };
  
  // Generate srcSet for responsive images
  const generateSrcSet = () => {
    if (!imageUrl) return undefined;
    
    const widths = [640, 768, 1024, 1280, 1536, 1920];
    
    return widths
      .map(w => `https:${imageUrl}?w=${w}&q=80&fm=webp ${w}w`)
      .join(', ');
  };
  
  const srcSet = generateSrcSet();
  
  // If we have a caption, wrap in figure
  if (imageCaption) {
    return (
      <figure className={className} style={style}>
        <img
          src={imageSrc}
          srcSet={srcSet}
          sizes={sizes}
          alt={imageAlt}
          loading={loading}
          onLoad={onLoad}
          onError={onError}
          style={{ objectPosition: `${focalPoint.x * 100}% ${focalPoint.y * 100}%` }}
        />
        <figcaption>
          {imageCaption}
          {imageCredit && <>
            <span> — </span>
            <small>{imageCredit}</small>
          </>}
        </figcaption>
      </figure>
    );
  }
  
  return (
    <img
      src={imageSrc}
      srcSet={srcSet}
      sizes={sizes}
      alt={imageAlt}
      className={className}
      style={{
        ...style,
        objectPosition: `${focalPoint.x * 100}% ${focalPoint.y * 100}%`
      }}
      loading={loading}
      onLoad={onLoad}
      onError={onError}
    />
  );
};

interface ContentfulBackgroundImageProps {
  imageKey?: string;
  fallbackSrc: string;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  overlay?: boolean;
  overlayOpacity?: number;
}

export const ContentfulBackgroundImage: React.FC<ContentfulBackgroundImageProps> = ({
  imageKey,
  fallbackSrc,
  className,
  style,
  children,
  overlay = false,
  overlayOpacity = 0.4
}) => {
  const { data: imageData } = useContentfulImage(imageKey || '');
  
  const contentfulImage = imageData?.[0] as any;
  const imageAsset = contentfulImage?.fields as any;
  
  // Safely access nested image fields
  const imageField = imageAsset?.image as any;
  const imageUrl = imageField?.fields?.file?.url;
  
  const imageSrc = imageUrl 
    ? `https:${imageUrl}?w=1920&q=80&fm=webp`
    : fallbackSrc;
  
  return (
    <div
      className={className}
      style={{
        ...style,
        backgroundImage: overlay 
          ? `linear-gradient(rgba(0, 0, 0, ${overlayOpacity}), rgba(0, 0, 0, ${overlayOpacity})), url('${imageSrc}')`
          : `url('${imageSrc}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      {children}
    </div>
  );
};